import { useEffect, useMemo, useState } from 'react';
import { Settings, ChevronDown, ChevronUp } from 'lucide-react';
import { sendToTerminal } from '../../lib/terminalLogger';
import { RequestService } from '../../lib/corel/actions/request';
import { VBCompilerService } from '../../lib/corel/actions/vbcompiler';
import type { VBCompilerProgress } from '../../lib/corel/actions/vbcompiler.types';
import { registerExecution } from '../../lib/registerExecution';
import { useToolData } from './hooks/useToolData';
import { useNotifications } from '../feedback/NotificationProvider';
import { notifyToolsError } from '../../lib/toolsErrorNotification';
import {
  DEFAULT_ONEBITMAP_SHARED_CONFIG,
  type OneBitmapBackground,
  type OneBitmapColorMode,
  type OneBitmapMaterialId,
} from '../../lib/onebitmap/sharedConfig';
import { useOneBitmapSharedConfig } from './hooks/useOneBitmapSharedConfig';

// Timeout específico para conversão de bitmap (20 minutos)
const BITMAP_CONVERSION_TIMEOUT_MS = 20 * 60 * 1000; // 20 minutos

interface ToolResponse {
  id: string;
  slug: string;
  engine: string;
  version: number;
  code: string;
  parametersSchema: {
    type: string;
    properties: {
      colorMode: { type: string; enum: string[]; default: string };
      transparent: { type: string; default: boolean };
      dpi: { type: string; minimum: number; maximum: number; default: number };
    };
    required: string[];
  };
  defaults: {
    colorMode: string;
    transparent: boolean;
    dpi: number;
  };
  updatedAt: string;
  checksum: string;
  config?: {
    action: string;
    vbid: string;
    progress: boolean;
    progressEvery: number;
    progressIntervalMs: number;
    progressLabel: string;
    progressStartEnd?: boolean;
    uiModal: boolean;
    uiCancelable: boolean;
    disablePerformanceMonitor?: boolean;
  };
}

export function OneBitmapV1Page() {
  const { notify } = useNotifications();
  // Estados inicializados com valores padrão que serão sobrescritos pelos defaults da API
  const [dpi, setDpi] = useState<string>('300');
  const [colorMode, setColorMode] = useState<OneBitmapColorMode>('CMYK');
  const [background, setBackground] = useState<OneBitmapBackground>('transparent');
  const [running, setRunning] = useState<boolean>(false);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [execError, setExecError] = useState<string | null>(null);
  const [progressState, setProgressState] = useState<{ current: number; total: number; percent?: number; message?: string } | null>(null);
  const [timeoutExceeded, setTimeoutExceeded] = useState<boolean>(false);

  // Material selecionado com DPI predefinido
  const [selectedMaterial, setSelectedMaterial] = useState<OneBitmapMaterialId>('dtf-textil');

  // Estados para seção avançada
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const [useCustomDpi, setUseCustomDpi] = useState<boolean>(false);
  const [customDpi, setCustomDpi] = useState<string>('');

  // ✅ PADRONIZADO: Usar useToolData hook centralizado com ETag
  // useETag: true (padrão) - cache inteligente, só baixa código novo se mudou no servidor
  const { data: toolData, loading, error, fromCache, refresh } = useToolData<ToolResponse>({
    slug: 'converter-para-bitmap',
    // useETag: true é o padrão - envia If-None-Match, recebe 304 se não mudou
    validate: (data) => {
      // Validação adicional específica desta ferramenta
      if (!data.parametersSchema?.properties) return false;
      return true;
    }
  });

  const {
    config: sharedConfig,
    error: sharedConfigError,
  } = useOneBitmapSharedConfig();

  const effectiveSharedConfig = sharedConfig ?? DEFAULT_ONEBITMAP_SHARED_CONFIG;
  const orderedMaterials = useMemo(
    () => [...effectiveSharedConfig.materials].sort((left, right) => left.sortOrder - right.sortOrder),
    [effectiveSharedConfig],
  );

  const materialDpiMap = useMemo(
    () =>
      orderedMaterials.reduce<Record<string, string>>((accumulator, material) => {
        accumulator[material.id] = String(material.dpi);
        return accumulator;
      }, {}),
    [orderedMaterials],
  );

  // Estado para feedback visual do refresh
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefreshTool = async () => {
    setIsRefreshing(true);
    await refresh();
    setIsRefreshing(false);
  };

  useEffect(() => {
    if (orderedMaterials.length === 0) {
      return;
    }

    const hasSelectedMaterial = orderedMaterials.some((material) => material.id === selectedMaterial);
    if (!hasSelectedMaterial) {
      setSelectedMaterial(orderedMaterials[0].id);
    }
  }, [orderedMaterials, selectedMaterial]);

  // Sincronizar DPI baseado no material selecionado (se não usar DPI customizado)
  useEffect(() => {
    if (!useCustomDpi) {
      const materialDpi = materialDpiMap[selectedMaterial] || '300';
      setDpi(materialDpi);
    }
  }, [materialDpiMap, selectedMaterial, useCustomDpi]);

  // Aplicar DPI customizado quando habilitado
  useEffect(() => {
    if (useCustomDpi && customDpi) {
      setDpi(customDpi);
    }
  }, [useCustomDpi, customDpi]);

  // Listener para Enter key - aplica quando pressionado
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignorar se estiver executando
      if (running) return;

      // Ignorar se dados da ferramenta não carregaram ainda
      if (loading || error || !toolData?.code) return;

      // Ignorar se estiver em um campo de texto ou textarea
      const target = e.target as HTMLElement;
      if (target.tagName === 'TEXTAREA') return;

      if (e.key === 'Enter') {
        e.preventDefault();
        handleApply();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [running, loading, error, toolData, dpi, colorMode, background, selectedMaterial, useCustomDpi, customDpi]);

  useEffect(() => {
    const toolColorMode = toolData?.defaults?.colorMode;
    const nextColorMode =
      sharedConfig?.defaults.colorMode ??
      (toolColorMode === 'CMYK' || toolColorMode === 'RGB'
        ? toolColorMode
        : DEFAULT_ONEBITMAP_SHARED_CONFIG.defaults.colorMode);

    const toolBackground =
      typeof toolData?.defaults?.transparent === 'boolean'
        ? (toolData.defaults.transparent ? 'transparent' : 'solid')
        : null;
    const nextBackground =
      sharedConfig?.defaults.background ??
      toolBackground ??
      DEFAULT_ONEBITMAP_SHARED_CONFIG.defaults.background;

    setColorMode(nextColorMode);
    setBackground(nextBackground);
  }, [sharedConfig, toolData]);

  // Função de validação dos campos
  const validateFields = () => {
    const errors: string[] = [];

    // Usar limites da API se disponível, senão usar valores padrão
    const minDpi = toolData?.parametersSchema?.properties?.dpi?.minimum || 72;
    const maxDpi = toolData?.parametersSchema?.properties?.dpi?.maximum || 1200;
    const validColorModes = toolData?.parametersSchema?.properties?.colorMode?.enum || ['CMYK', 'RGB'];

    // Validar DPI
    const dpiValue = parseInt(dpi);
    if (isNaN(dpiValue) || dpiValue < minDpi || dpiValue > maxDpi) {
      errors.push(`DPI deve estar entre ${minDpi} e ${maxDpi}`);
    }

    // Validar colorMode
    if (!validColorModes.includes(colorMode)) {
      errors.push(`Modo de cor deve ser ${validColorModes.join(' ou ')}`);
    }

    // Validar background
    if (!['solid', 'transparent'].includes(background)) {
      errors.push('Fundo deve ser solid ou transparent');
    }

    return errors;
  };

  // Função para aplicar a conversão de bitmap
  const handleApply = async () => {
    // Validar campos
    const errors = validateFields();
    if (errors.length > 0) {
      alert('Erros de validação:\n' + errors.join('\n'));
      return;
    }

    // Preparar parâmetros seguindo o contrato da API/host
    const parameters = {
      dpi: parseInt(dpi),
      colorMode,
      transparent: background === 'transparent'
    } as const;

    if (!toolData?.code) {
      alert('Código da ferramenta indisponível. Recarregue a página.');
      return;
    }

    setExecError(null);
    setRunning(true);
    setTimeoutExceeded(false);
    setProgressState({ current: 0, total: 0, percent: 0, message: 'Iniciando…' });

    // Definir timeout customizado de 20 minutos para vbcompiler ANTES de usar
    const customTimeoutMs = BITMAP_CONVERSION_TIMEOUT_MS;

    // Timer para detectar quando timeout foi excedido
    const timeoutTimer = window.setTimeout(() => {
      if (running) {
        setTimeoutExceeded(true);
        sendToTerminal('⚠️ [VB] Timeout excedido (20 minutos)', { 
          timeoutMs: BITMAP_CONVERSION_TIMEOUT_MS
        });
      }
    }, BITMAP_CONVERSION_TIMEOUT_MS);

    const payload = {
      vbid: toolData.config?.vbid || String(toolData.slug || 'convert_to_bitmap'),
      code: String(toolData.code || ''),
      parameters,
      progress: toolData.config?.progress ?? true,
      progressEvery: toolData.config?.progressEvery ?? 3,
      progressIntervalMs: toolData.config?.progressIntervalMs ?? 150,
      progressLabel: toolData.config?.progressLabel ?? 'Conversão/Bitmap',
      progressStartEnd: toolData.config?.progressStartEnd ?? true,
      uiModal: toolData.config?.uiModal ?? true,
      uiCancelable: toolData.config?.uiCancelable ?? true,
      disablePerformanceMonitor: toolData.config?.disablePerformanceMonitor ?? false
    };
    
    // ✅ ATUALIZADO: Usando VBCompilerService centralizado
    const result = await VBCompilerService.execute(payload, {
      timeoutMs: customTimeoutMs,
      onProgress: (progress: VBCompilerProgress) => {
        setProgressState({
          current: progress.current,
          total: progress.total,
          percent: progress.percent,
          message: progress.message
        });
      },
      onComplete: (response) => {
        // Registrar execução na API
        try {
          registerExecution(
            {
              requestId: response.requestId || result.requestId || 'unknown',
              status: 'ok',
              result: response.result
            },
            toolData,
            parameters
          ).catch((err) => {
            sendToTerminal('⚠️ [ONEBITMAP] Erro ao registrar execução', {
              error: String(err)
            });
          });
        } catch {}
        
        window.clearTimeout(timeoutTimer);
        setRunning(false);
        setProgressState(null);
        setRequestId(result.requestId || null);
        setTimeoutExceeded(false);
      },
      onError: (error: string) => {
        let errorMsg = error || 'Falha ao executar a ferramenta';
        
        // Tratamento específico para timeout
        if (result.status === 'timeout' || error.toLowerCase().includes('timeout')) {
          errorMsg = `Conversão demorou mais que 20 minutos. Tente com menos objetos selecionados ou DPI menor.`;
        }
        
        sendToTerminal('❌ [VB] Erro na execução', { 
          requestId: result.requestId, 
          message: errorMsg, 
          status: result.status
        });
        
        // Registrar execução com erro na API
        try {
          registerExecution(
            {
              requestId: result.requestId || 'unknown',
              status: result.status || 'error',
              message: errorMsg
            },
            toolData,
            parameters
          ).catch((err) => {
            sendToTerminal('⚠️ [VB] Erro ao registrar execução com erro', {
              error: String(err)
            });
          });
        } catch {}
        
        window.clearTimeout(timeoutTimer);
        
        // ✅ MIGRADO: Usar notifyToolsError para tratamento centralizado
        notifyToolsError(new Error(errorMsg), notify, 'tool');
        setExecError(errorMsg);
        setRunning(false);
        setProgressState(null);
        setRequestId(result.requestId || null);
        setTimeoutExceeded(false);
      }
    });

    if (result.requestId) {
      setRequestId(result.requestId);
    }
  };

  const handleCancel = async () => {
    try {
      if (requestId) {
        // ✅ ATUALIZADO: Usando RequestService centralizado
        RequestService.cancel(requestId);
        setProgressState((p) => ({ ...(p || { current: 0, total: 0 }), message: 'Cancelando…' }));
      }
    } catch {}
  };

  // Mostrar loading
  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="text-sm text-gray-300 mb-2">Carregando configurações...</div>
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      </div>
    );
  }

  // Mostrar erro
  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center p-4">
          <div className="text-sm text-red-300 mb-2">Erro ao carregar ferramenta</div>
          <div className="text-xs text-gray-400 mb-3">{error}</div>
          <button 
            onClick={() => window.location.reload()} 
            className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-3 pb-14 space-y-3">

        {/* Seção: Material */}
        <section className="bg-navy-800/60 border border-navy-700 rounded-lg p-3">
          <div className="text-sm text-gray-300 font-medium mb-3">Tipo de Material</div>

          {/* Botões de Material - grid 2x2 */}
          <div className="grid grid-cols-2 gap-2">
            {orderedMaterials.map((material) => (
              <button
                key={material.id}
                type="button"
                onClick={() => {
                  setSelectedMaterial(material.id);
                  setUseCustomDpi(false);
                }}
                className={`py-2 px-3 text-xs font-medium rounded-lg border transition-all ${
                  selectedMaterial === material.id && !useCustomDpi
                    ? 'bg-blue-600 border-blue-500 text-white shadow-md'
                    : 'bg-navy-800 border-navy-700 text-gray-300 hover:bg-navy-700 hover:border-navy-600'
                }`}
              >
                {material.label}
              </button>
            ))}
          </div>
        </section>

        {/* Seção: Configurações */}
        <section className="bg-navy-800/60 border border-navy-700 rounded-lg p-3">
          <div className="text-sm text-gray-300 font-medium mb-3">Configurações</div>

          {/* Modo de Cor e Fundo em grid */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Modo de Cor</label>
              <select
                value={colorMode}
                onChange={(e) => setColorMode(e.target.value as OneBitmapColorMode)}
                className="w-full bg-navy-900 border border-navy-600 rounded px-2 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
              >
                {(toolData?.parametersSchema?.properties?.colorMode?.enum || ['CMYK', 'RGB']).map((mode) => (
                  <option key={mode} value={mode}>{mode}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Fundo</label>
              <select
                value={background}
                onChange={(e) => setBackground(e.target.value as OneBitmapBackground)}
                className="w-full bg-navy-900 border border-navy-600 rounded px-2 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
              >
                <option value="transparent">Transparente</option>
                <option value="solid">Sólido</option>
              </select>
            </div>
          </div>
        </section>

        {/* Seção: Avançado (colapsável) */}
        <section className="bg-navy-800/60 border border-navy-700 rounded-lg">
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="w-full flex items-center justify-between p-3 text-left hover:bg-navy-700/30 transition-colors rounded-lg"
          >
            <div className="flex items-center gap-2">
              <Settings className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-300 font-medium">Avançado</span>
            </div>
            {showAdvanced ? (
              <ChevronUp className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            )}
          </button>

          {showAdvanced && (
            <div className="px-3 pb-3 space-y-3 border-t border-navy-700 pt-3">
              {/* Checkbox para DPI personalizado */}
              <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useCustomDpi}
                  onChange={(e) => {
                    setUseCustomDpi(e.target.checked);
                    if (!e.target.checked) {
                      // Voltar para DPI do material
                      setDpi(materialDpiMap[selectedMaterial] || '300');
                    }
                  }}
                  className="rounded border-navy-600 bg-navy-900 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
                />
                Usar DPI personalizado
              </label>

              {/* Campo de DPI personalizado */}
              {useCustomDpi && (
                <div>
                  <label className="block text-xs text-gray-400 mb-1">
                    DPI ({toolData?.parametersSchema?.properties?.dpi?.minimum || 72}-{toolData?.parametersSchema?.properties?.dpi?.maximum || 1200})
                  </label>
                  <input
                    type="number"
                    min={toolData?.parametersSchema?.properties?.dpi?.minimum || 72}
                    max={toolData?.parametersSchema?.properties?.dpi?.maximum || 1200}
                    value={customDpi}
                    onChange={(e) => {
                      setCustomDpi(e.target.value);
                      setDpi(e.target.value);
                    }}
                    placeholder={`Ex: ${toolData?.defaults?.dpi || 400}`}
                    className="w-full bg-navy-900 border border-navy-600 rounded px-2 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
                  />
                </div>
              )}

              {/* Info resumida */}
              <div className="text-[10px] text-gray-500 space-y-0.5 pt-1">
                <div>• Timeout: 20 min • Converte apenas seleção</div>
                {sharedConfigError && (
                  <div className="text-yellow-400">
                    • Configuração compartilhada indisponível. Usando fallback seguro.
                  </div>
                )}
                {toolData && (
                  <div className="flex items-center gap-2">
                    <span>• Versão: {toolData.version}</span>
                    {fromCache !== undefined && (
                      <span className={`text-[9px] px-1.5 py-0.5 rounded ${fromCache ? 'bg-green-900/50 text-green-300' : 'bg-blue-900/50 text-blue-300'}`}>
                        {fromCache ? 'cache' : 'novo'}
                      </span>
                    )}
                    <button
                      onClick={handleRefreshTool}
                      disabled={isRefreshing || running}
                      className="text-blue-400 hover:text-blue-300 underline"
                      title="Atualizar código da ferramenta"
                    >
                      {isRefreshing ? '...' : 'atualizar'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </section>

        {/* Botões de Ação */}
        <div className="pt-1">
          <button
            className={`w-full ${running ? 'bg-blue-700 opacity-70 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-500'} text-white font-medium py-2.5 px-4 rounded-lg transition-colors text-sm`}
            onClick={running ? undefined : handleApply}
            disabled={running}
          >
            {running ? 'Executando…' : 'Aplicar'}
          </button>
          {execError && (
            <div className="mt-2 text-xs text-red-300 text-center">{execError}</div>
          )}
        </div>
      </div>

      {running && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-navy-800 border border-navy-700 rounded-lg p-4 w-[360px]">
            <div className="text-sm text-gray-100 mb-2">Executando Conversão/Bitmap</div>
            
            {/* Mensagem de timeout excedido */}
            {timeoutExceeded && (
              <div className="mb-3 p-2 bg-yellow-900/30 border border-yellow-700/50 rounded text-[11px] text-yellow-300">
                ⏰ <strong>Tempo limite excedido (20 min)</strong><br/>
                A conversão continua em execução. Aguarde a conclusão.
              </div>
            )}
            
            <div className="w-full h-2 bg-navy-900 rounded overflow-hidden mb-2">
              <div
                className="h-full bg-blue-600 transition-all"
                style={{ width: `${Math.max(0, Math.min(100, progressState?.percent ?? 0))}%` }}
              />
            </div>
            <div className="text-[11px] text-gray-400 mb-3">
              {progressState?.message || 'Aguarde…'}
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={handleCancel}
                className="px-3 py-1.5 text-xs rounded border border-navy-600 text-gray-300 hover:bg-navy-700"
              >Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
